import type { UserRole } from './types/user';

export const FEATURE_PERMISSION_ROLES = [
  'hq_admin',
  'agent',
  'affiliate_owner',
  'affiliate_coach',
  'member',
  'individual',
  'purchaser',
] as const satisfies readonly UserRole[];

export const EDITABLE_FEATURE_PERMISSION_ROLES = FEATURE_PERMISSION_ROLES.filter(
  (role): role is Exclude<UserRole, 'purchaser'> => role !== 'purchaser'
);

const FEATURE_PERMISSION_CATALOG = [
  {
    key: 'approve_affiliates',
    label: 'Approve Affiliates',
    description: 'Approve/reject affiliate applications',
    comingSoon: false,
  },
  {
    key: 'upload_programming',
    label: 'Upload Programming',
    description: 'Create and manage workout programming',
    comingSoon: false,
  },
  {
    key: 'view_system_metrics',
    label: 'View System Metrics',
    description: 'Access platform-wide analytics',
    comingSoon: false,
  },
  {
    key: 'manage_users',
    label: 'Manage Users',
    description: 'Add, edit, suspend users',
    comingSoon: false,
  },
  {
    key: 'invite_members',
    label: 'Invite Members',
    description: 'Send member invitations',
    comingSoon: false,
  },
  {
    key: 'log_scores',
    label: 'Log Scores',
    description: 'Record workout results',
    comingSoon: false,
  },
  {
    key: 'view_leaderboard',
    label: 'View Leaderboard',
    description: 'View competition rankings',
    comingSoon: false,
  },
  {
    key: 'create_events',
    label: 'Create Events',
    description: 'Create gym/platform events',
    comingSoon: false,
  },
  {
    key: 'sign_up_events',
    label: 'Sign Up Events',
    description: 'Register for events',
    comingSoon: false,
  },
  {
    key: 'manage_billing',
    label: 'Manage Billing',
    description: 'Access billing/subscription settings',
    comingSoon: false,
  },
  {
    key: 'access_assets',
    label: 'Access Assets',
    description: 'Download marketing/training resources',
    comingSoon: false,
  },
  {
    key: 'view_commissions',
    label: 'View Commissions',
    description: 'View commission reports',
    comingSoon: false,
  },
  {
    key: 'device_linking',
    label: 'Device Linking',
    description: 'Link erg devices',
    comingSoon: true,
  },
  {
    key: 'global_rank',
    label: 'Global Rank',
    description: 'View global rankings',
    comingSoon: true,
  },
] as const;

export type FeaturePermissionKey = (typeof FEATURE_PERMISSION_CATALOG)[number]['key'];
export type FeaturePermissions = Record<FeaturePermissionKey, boolean>;

export interface FeaturePermissionDefinition {
  key: FeaturePermissionKey;
  label: string;
  description: string;
  comingSoon: boolean;
}

export const FEATURE_PERMISSION_DEFINITIONS: readonly FeaturePermissionDefinition[] =
  FEATURE_PERMISSION_CATALOG;
export const FEATURE_PERMISSION_KEYS = FEATURE_PERMISSION_CATALOG.map(
  ({ key }) => key
) as unknown as readonly [FeaturePermissionKey, ...FeaturePermissionKey[]];

const permissions = (enabled: readonly FeaturePermissionKey[] = []): FeaturePermissions =>
  Object.fromEntries(
    FEATURE_PERMISSION_KEYS.map((key) => [key, enabled.includes(key)])
  ) as FeaturePermissions;

export const DEFAULT_FEATURE_PERMISSIONS: Record<UserRole, FeaturePermissions> = {
  hq_admin: permissions([
    'approve_affiliates',
    'upload_programming',
    'view_system_metrics',
    'manage_users',
    'invite_members',
    'log_scores',
    'view_leaderboard',
    'create_events',
    'sign_up_events',
    'access_assets',
    'view_commissions',
  ]),
  agent: permissions([
    'log_scores',
    'view_leaderboard',
    'create_events',
    'sign_up_events',
    'access_assets',
    'view_commissions',
  ]),
  affiliate_owner: permissions([
    'manage_users',
    'invite_members',
    'log_scores',
    'view_leaderboard',
    'create_events',
    'manage_billing',
    'access_assets',
  ]),
  affiliate_coach: permissions(['log_scores', 'view_leaderboard', 'access_assets']),
  member: permissions(['log_scores', 'view_leaderboard', 'sign_up_events']),
  individual: permissions(['log_scores', 'view_leaderboard', 'manage_billing']),
  purchaser: permissions(),
};

export const EMPTY_FEATURE_PERMISSIONS = permissions();

export const COACH_FLAG_FEATURE_GRANTS = {
  can_invite: 'invite_members',
  can_create_events: 'create_events',
  can_view_billing: 'manage_billing',
  can_manage_coaches: 'manage_users',
  can_manage_members: 'manage_users',
} as const;
